import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    required,
    maxLength,
    useTranslate,
    useRedirect,
    useNotify,
    Toolbar,
    SaveButton,
    DeleteButton,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
} from '@mui/material';
import { ArrowBack, Groups } from '@mui/icons-material';

const EditToolbar = () => (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#FAFAFA', borderTop: '1px solid #E0E0E0', px: { xs: 2, sm: 3 }, py: 2, mt: { xs: 2, sm: 4 } }}>
        <SaveButton variant="contained" />
        <DeleteButton />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/user-groups')}
                size="large"
                sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('resources.user-groups.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.user-groups.edit.title')}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }} color="text.secondary">
                    {translate('resources.user-groups.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const UserGroupEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Edit
            title={false}
            component="div"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    notify('resources.user-groups.notifications.updated', { type: 'success' });
                    redirect('/user-groups');
                },
                onError: () => {
                    notify('resources.user-groups.notifications.error.update', { type: 'error' });
                },
            }}
        >
            <SimpleForm toolbar={<EditToolbar />}>
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 } }}>
                    <PageHeader />
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Groups sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.user-groups.sections.basic')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    validate={[
                                        required(),
                                        maxLength(100, translate('resources.user-groups.validation.nameTooLong')),
                                    ]}
                                    fullWidth
                                    helperText={translate('resources.user-groups.helpers.name')}
                                    label={translate('resources.user-groups.fields.name')}
                                />
                                <TextInput
                                    source="description"
                                    multiline
                                    rows={4}
                                    validate={[
                                        maxLength(500, translate('resources.user-groups.validation.descriptionTooLong')),
                                    ]}
                                    fullWidth
                                    helperText={translate('resources.user-groups.helpers.description')}
                                    label={translate('resources.user-groups.fields.description')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};

export default UserGroupEdit;
